import React, {
  Suspense,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react"
import { useDispatch, useSelector } from "react-redux"
import { Link, useNavigate } from "react-router-dom"
import {
  Badge,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  Col,
  Collapse,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  Spinner,
  UncontrolledButtonDropdown,
} from "reactstrap"
import { exportProvinceRealtimeDashboard } from "store/province-dashboard/province-dashboard-action"
import Swal from "sweetalert2"
import ProviderDashboardFilterPanel from "components/ProviderDashboard/ProviderDashboardFilterPanel"
import AfghanistanProvinceMapPanel from "components/ProviderDashboard/AfghanistanProvinceMapPanel"
import ProvinceStockComponents from "./ProvinceStock"
import ProvinceStockItemList from "./ProvinceStockItemList"
import {
  fetchProvinceRealtimeDashboardConfig,
  fetchSectionChartData,
  fetchSectionMapData,
  getProvinceStock,
} from "store/province-dashboard/province-dashboard-action"
import { provinceDashboardAction } from "store/province-dashboard/province-dashboard-slice"
import {
  hasActiveDashboardFilters,
  serializeFilterParams,
} from "utils/providerDashboardFilters"
const ProviderDashboardBarChart = React.lazy(() =>
  import("components/ProviderDashboard/charts/ProviderDashboardBarChart")
)
const ProviderDashboardLineChart = React.lazy(() =>
  import("components/ProviderDashboard/charts/ProviderDashboardLineChart")
)
const ProviderDashboardPieChart = React.lazy(() =>
  import("components/ProviderDashboard/charts/ProviderDashboardPieChart")
)
const ProviderDashboardStackedBarChart = React.lazy(() =>
  import("components/ProviderDashboard/charts/ProviderDashboardStackedBarChart")
)

const FILTER_DEBOUNCE_MS = 400

const FilterSection = ({
  title,
  description,
  definitions,
  options,
  onChange,
  onReset,
  children,
  activeContexts = null,
}) => {
  const dispatch = useDispatch()
  const [open, setOpen] = useState(false)
  const [localFilters, setLocalFilters] = useState({})
  const configLoading = useSelector(
    state => state.provinceDashboard.realtime?.config?.loading
  )
  const configError = useSelector(
    state => state.provinceDashboard.realtime?.config?.error
  )

  const handleOptionsRefresh = useCallback(
    nextFilters => dispatch(fetchProvinceRealtimeDashboardConfig(nextFilters, true)),
    [dispatch]
  )

  const activeCount = Object.keys(localFilters).filter(k => {
    const v = localFilters[k]
    return (
      v !== null &&
      v !== undefined &&
      v !== "" &&
      !(Array.isArray(v) && v.length === 0)
    )
  }).length

  return (
    <Card className="mt-3">
      <CardHeader>
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <div>
            <h5 className="mb-0">{title}</h5>
            {description ? (
              <p className="text-muted mb-0 small">{description}</p>
            ) : null}
          </div>
          <div className="d-flex gap-2 align-items-center">
            {activeCount > 0 && !open ? (
              <Badge color="primary" pill>
                {activeCount} active
              </Badge>
            ) : null}
            <Button
              size="sm"
              color={open ? "primary" : "success"}
              onClick={() => setOpen(c => !c)}
            >
              {open ? "Hide Filters" : "Filters"}
            </Button>
          </div>
        </div>
        {open ? (
          <div className="mt-3 provider-dashboard-filter-body">
            {configLoading && !definitions.length ? (
              <div className="text-center py-4">
                <Spinner size="sm" /> Loading filter options...
              </div>
            ) : configError ? (
              <div className="alert alert-danger mb-0">
                Unable to load filter options. Please refresh the page or
                contact support.
              </div>
            ) : definitions.length === 0 ? (
              <div className="alert alert-warning mb-0">
                Filter fields are not available yet. Please wait or refresh the
                page.
              </div>
            ) : (
              <ProviderDashboardFilterPanel
                definitions={definitions}
                options={options}
                initialFilters={localFilters}
                loading={configLoading}
                optionsLoading={configLoading}
                onOptionsRefresh={handleOptionsRefresh}
                activeContexts={activeContexts}
                onChange={nextFilters => {
                  setLocalFilters(nextFilters)
                  onChange(nextFilters)
                }}
                onClearAll={() => {
                  setLocalFilters({})
                  onReset()
                }}
              />
            )}
          </div>
        ) : null}
      </CardHeader>
      <CardBody>{children}</CardBody>
    </Card>
  )
}

const ProvinceDashboardComponent = ({
  stocks,
  onAdvanceSearch,
  onCategoryChange,
  categories,
  onExportClicked,
  onAdvancedSearchHandle,
  allData,
}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const user = useSelector(state => state.auth?.user)
  const realtime = useSelector(state => state.provinceDashboard.realtime) || {}
  const config = realtime.config || { loading: false, error: null, data: null }

  const defaultFilters = useMemo(() => {
    const ids = user?.item_categories
    return ids?.length ? { item_category_ids: ids } : {}
  }, [user?.item_categories])

  const hasCategoryAccess = !!user?.item_categories?.length
  const sectionChartData =
    useSelector(state => state.provinceDashboard.sectionChartData) || {}

  const [stockListOpen, setStockListOpen] = useState(false)
  const [stackedBarListOpen, setStackedBarListOpen] = useState(false)
  const [lineListOpen, setLineListOpen] = useState(false)
  const [pieListOpen, setPieListOpen] = useState(false)
  const [localMapType, setLocalMapType] = useState("receipt")

  const provinceStockData = useSelector(
    state => state.provinceDashboard.provinceStock
  )
  const provinceStockLoading = useSelector(
    state => state.provinceDashboard.provinceStock?.loading
  )

  const definitions = useMemo(
    () => config.data?.definitions || [],
    [config.data?.definitions]
  )
  const options = useMemo(
    () => config.data?.options || {},
    [config.data?.options]
  )

  const barSection = sectionChartData.bar || {
    loading: false,
    error: null,
    data: null,
    dataset: "stock",
    hasData: false,
  }
  const lineSection = sectionChartData.line || {
    loading: false,
    error: null,
    data: null,
    dataset: "stock",
    hasData: false,
  }
  const pieSection = sectionChartData.pie || {
    loading: false,
    error: null,
    data: null,
    dataset: "stock",
    hasData: false,
  }
  const mapSection = sectionChartData.map || {
    loading: false,
    error: null,
    data: null,
    type: "receipt",
    hasData: false,
  }
  const stackedBarSection = sectionChartData.stackedBar || {
    loading: false,
    error: null,
    data: null,
    dataset: "stock",
    hasData: false,
  }

  const barFiltersRef = useRef({})
  const barDebounceRef = useRef(null)
  const lineFiltersRef = useRef({})
  const lineDebounceRef = useRef(null)
  const pieFiltersRef = useRef({})
  const pieDebounceRef = useRef(null)
  const mapFiltersRef = useRef({})
  const mapDebounceRef = useRef(null)
  const mapTypeRef = useRef("receipt")
  const stackedBarFiltersRef = useRef({})
  const stackedBarDebounceRef = useRef(null)

  const [exportDataset, setExportDataset] = useState("stock")
  const [exportingFormat, setExportingFormat] = useState(null)

  const handleExport = useCallback(
    format => {
      setExportingFormat(format)
      const combinedFilters = {
        ...barFiltersRef.current,
        ...lineFiltersRef.current,
        ...pieFiltersRef.current,
        ...stackedBarFiltersRef.current,
        ...mapFiltersRef.current,
      }
      dispatch(
        exportProvinceRealtimeDashboard({
          filters: combinedFilters,
          dataset: exportDataset,
          format,
        })
      )
        .then(() => {
          Swal.fire({
            position: "top-end",
            icon: "success",
            title: `${format.toUpperCase()} export completed`,
            showConfirmButton: false,
            timer: 3000,
            toast: true,
          })
        })
        .catch(() => {
          Swal.fire({
            position: "top-end",
            icon: "error",
            title: "Export failed. Please try again.",
            showConfirmButton: false,
            timer: 5000,
            toast: true,
          })
        })
        .finally(() => setExportingFormat(null))
    },
    [dispatch, exportDataset]
  )

  useEffect(() => {
    dispatch(fetchProvinceRealtimeDashboardConfig(defaultFilters))

    if (!hasCategoryAccess) {
      dispatch(getProvinceStock({}))
      return
    }

    dispatch(getProvinceStock(defaultFilters))
    dispatch(
      fetchSectionChartData({
        filters: defaultFilters,
        section: "bar",
        chartType: "bar",
        dataset: "stock",
      })
    )
    dispatch(
      fetchSectionChartData({
        filters: defaultFilters,
        section: "line",
        chartType: "line",
        dataset: "distributions",
      })
    )
    dispatch(
      fetchSectionChartData({
        filters: defaultFilters,
        section: "pie",
        chartType: "pie",
        dataset: "stock",
      })
    )
    dispatch(
      fetchSectionChartData({
        filters: defaultFilters,
        section: "stackedBar",
        chartType: "bar",
        dataset: "stock",
      })
    )
  }, [dispatch, defaultFilters, hasCategoryAccess])

  useEffect(() => {
    return () => {
      clearTimeout(barDebounceRef.current)
      clearTimeout(lineDebounceRef.current)
      clearTimeout(pieDebounceRef.current)
      clearTimeout(mapDebounceRef.current)
      clearTimeout(stackedBarDebounceRef.current)
    }
  }, [])

  const handleBarFilter = useCallback(
    nextFilters => {
      barFiltersRef.current = nextFilters
      clearTimeout(barDebounceRef.current)
      barDebounceRef.current = setTimeout(() => {
        dispatch(
          fetchSectionChartData({
            filters: nextFilters,
            section: "bar",
            chartType: "bar",
            dataset: "stock",
          })
        )
        dispatch(getProvinceStock(nextFilters))
      }, FILTER_DEBOUNCE_MS)
    },
    [dispatch]
  )

  const resetBarFilter = useCallback(() => {
    barFiltersRef.current = {}
    clearTimeout(barDebounceRef.current)
    dispatch(
      fetchSectionChartData({
        filters: {},
        section: "bar",
        chartType: "bar",
        dataset: "stock",
      })
    )
    dispatch(getProvinceStock({}))
  }, [dispatch])

  const handleLineFilter = useCallback(
    nextFilters => {
      lineFiltersRef.current = nextFilters
      clearTimeout(lineDebounceRef.current)
      lineDebounceRef.current = setTimeout(() => {
        dispatch(
          fetchSectionChartData({
            filters: nextFilters,
            section: "line",
            chartType: "line",
            dataset: "distributions",
          })
        )
        dispatch(getProvinceStock(nextFilters))
      }, FILTER_DEBOUNCE_MS)
    },
    [dispatch]
  )

  const resetLineFilter = useCallback(() => {
    lineFiltersRef.current = {}
    clearTimeout(lineDebounceRef.current)
    dispatch(
      fetchSectionChartData({
        filters: {},
        section: "line",
        chartType: "line",
        dataset: "distributions",
      })
    )
    dispatch(getProvinceStock({}))
  }, [dispatch])

  const handlePieFilter = useCallback(
    nextFilters => {
      pieFiltersRef.current = nextFilters
      clearTimeout(pieDebounceRef.current)
      pieDebounceRef.current = setTimeout(() => {
        dispatch(
          fetchSectionChartData({
            filters: nextFilters,
            section: "pie",
            chartType: "pie",
            dataset: "stock",
          })
        )
        dispatch(getProvinceStock(nextFilters))
      }, FILTER_DEBOUNCE_MS)
    },
    [dispatch]
  )

  const resetPieFilter = useCallback(() => {
    pieFiltersRef.current = {}
    clearTimeout(pieDebounceRef.current)
    dispatch(
      fetchSectionChartData({
        filters: {},
        section: "pie",
        chartType: "pie",
        dataset: "stock",
      })
    )
    dispatch(getProvinceStock({}))
  }, [dispatch])

  const handleMapFilter = useCallback(
    nextFilters => {
      mapFiltersRef.current = nextFilters
      clearTimeout(mapDebounceRef.current)
      mapDebounceRef.current = setTimeout(() => {
        if (!hasActiveDashboardFilters(nextFilters)) {
          dispatch(provinceDashboardAction.resetSectionChartData("map"))
          return
        }
        dispatch(
          fetchSectionMapData({
            filters: nextFilters,
            type: mapTypeRef.current,
          })
        )
      }, FILTER_DEBOUNCE_MS)
    },
    [dispatch]
  )

  const resetMapFilter = useCallback(() => {
    mapFiltersRef.current = {}
    clearTimeout(mapDebounceRef.current)
    dispatch(provinceDashboardAction.resetSectionChartData("map"))
  }, [dispatch])

  const handleMapTypeChange = useCallback(
    type => {
      mapTypeRef.current = type
      setLocalMapType(type)
      if (!hasActiveDashboardFilters(mapFiltersRef.current)) {
        return
      }
      dispatch(
        fetchSectionMapData({
          filters: mapFiltersRef.current,
          type,
        })
      )
    },
    [dispatch]
  )

  const handleProvinceClick = useCallback(
    provinceId => {
      const query = serializeFilterParams(mapFiltersRef.current, {
        type: mapTypeRef.current,
      })
      navigate(
        `/dashboard/ip-provider-province-detail/${provinceId}${query ? `?${query}` : ""}`
      )
    },
    [navigate]
  )

  const handleStackedBarFilter = useCallback(
    nextFilters => {
      stackedBarFiltersRef.current = nextFilters
      clearTimeout(stackedBarDebounceRef.current)
      stackedBarDebounceRef.current = setTimeout(() => {
        dispatch(
          fetchSectionChartData({
            filters: nextFilters,
            section: "stackedBar",
            chartType: "bar",
            dataset: "stock",
          })
        )
        dispatch(getProvinceStock(nextFilters))
      }, FILTER_DEBOUNCE_MS)
    },
    [dispatch]
  )

  const resetStackedBarFilter = useCallback(() => {
    stackedBarFiltersRef.current = {}
    clearTimeout(stackedBarDebounceRef.current)
    dispatch(
      fetchSectionChartData({
        filters: {},
        section: "stackedBar",
        chartType: "bar",
        dataset: "stock",
      })
    )
    dispatch(getProvinceStock({}))
  }, [dispatch])

  const renderChart = (section, ChartComponent, dataset) => {
    if (section.loading)
      return (
        <div className="text-center py-5">
          <Spinner />
        </div>
      )
    if (section.error)
      return (
        <div className="alert alert-danger mb-0">
          Unable to load chart data. Please try again.
        </div>
      )
    const chartData = section.data || []
    if (!chartData.length || !section.hasData)
      return (
        <div className="text-center py-5 text-muted">
          No data available for selected filters.
        </div>
      )
    return (
      <Suspense
        fallback={
          <div className="text-center py-5">
            <Spinner />
          </div>
        }
      >
        <ChartComponent data={chartData} dataset={dataset} variant="default" />
      </Suspense>
    )
  }

  const renderStackedBarChart = section => {
    if (section.loading)
      return (
        <div className="text-center py-5">
          <Spinner />
        </div>
      )
    if (section.error)
      return (
        <div className="alert alert-danger mb-0">
          Unable to load chart data. Please try again.
        </div>
      )
    const chartData = section.data || []
    if (!chartData.length || !section.hasData)
      return (
        <div className="text-center py-5 text-muted">
          No data available for selected filters.
        </div>
      )
    return (
      <Suspense
        fallback={
          <div className="text-center py-5">
            <Spinner />
          </div>
        }
      >
        <ProviderDashboardStackedBarChart data={chartData} />
      </Suspense>
    )
  }

  const renderStockDetails = (filtersRef, isOpen, setIsOpen, showLabel, hideLabel) => (
    <div className="mt-4">
      <Button
        size="sm"
        color="secondary"
        onClick={() => setIsOpen(c => !c)}
      >
        {isOpen ? hideLabel : showLabel}
      </Button>
      <Collapse isOpen={isOpen}>
        <div className="mt-3">
          {provinceStockLoading ? (
            <div className="text-center py-5">
              <Spinner />
            </div>
          ) : (
            <ProvinceStockItemList
              data={provinceStockData?.data?.data?.provinceReports}
              allData={provinceStockData?.data?.data}
              searchDate={{
                fromDate: filtersRef.current?.date_from || null,
                toDate: filtersRef.current?.date_to || null,
              }}
            />
          )}
        </div>
      </Collapse>
    </div>
  )

  return (
    <div>
      <Card>
        <CardBody>
          <Row className="align-items-center g-3">
            <Col md={5} lg={4}>
              <div>
                <h5 className="font-size-15 text-truncate mb-1">
                  {user?.name}
                </h5>
                <p className="text-muted mb-2 text-truncate">{user?.email}</p>
                <Link to="/profile" className="btn btn-primary btn-sm">
                  View Profile <i className="mdi mdi-arrow-right ms-1"></i>
                </Link>
              </div>
            </Col>
            <Col md={7} lg={8}>
              <div className="d-flex flex-column align-items-md-end gap-2">
                <div className="text-md-end">
                  <h4 className="card-title mb-1">IP Account Dashboard</h4>
                  <p className="text-muted mb-0">
                    Stock, receipt, distribution, and consumption overview
                  </p>
                </div>
                <div className="d-flex gap-2 flex-wrap align-items-center">
                  <UncontrolledButtonDropdown>
                    <DropdownToggle color="secondary" size="sm" caret>
                      Dataset:{" "}
                      {exportDataset.charAt(0).toUpperCase() +
                        exportDataset.slice(1)}
                    </DropdownToggle>
                    <DropdownMenu>
                      <DropdownItem
                        active={exportDataset === "stock"}
                        onClick={() => setExportDataset("stock")}
                      >
                        Stock
                      </DropdownItem>
                      <DropdownItem
                        active={exportDataset === "receipts"}
                        onClick={() => setExportDataset("receipts")}
                      >
                        Receipts
                      </DropdownItem>
                      <DropdownItem
                        active={exportDataset === "distributions"}
                        onClick={() => setExportDataset("distributions")}
                      >
                        Distributions
                      </DropdownItem>
                      <DropdownItem
                        active={exportDataset === "consumption"}
                        onClick={() => setExportDataset("consumption")}
                      >
                        Consumption
                      </DropdownItem>
                    </DropdownMenu>
                  </UncontrolledButtonDropdown>
                  <ButtonGroup>
                    <Button
                      type="button"
                      color="success"
                      outline
                      disabled={!!exportingFormat}
                      onClick={() => handleExport("xlsx")}
                    >
                      {exportingFormat === "xlsx" ? (
                        <Spinner size="sm" className="me-1" />
                      ) : null}
                      Excel
                    </Button>
                    <Button
                      color="info"
                      outline
                      disabled={!!exportingFormat}
                      onClick={() => handleExport("csv")}
                    >
                      {exportingFormat === "csv" ? (
                        <Spinner size="sm" className="me-1" />
                      ) : null}
                      CSV
                    </Button>
                    <Button
                      color="secondary"
                      outline
                      disabled={!!exportingFormat}
                      onClick={() => handleExport("pdf")}
                    >
                      {exportingFormat === "pdf" ? (
                        <Spinner size="sm" className="me-1" />
                      ) : null}
                      PDF
                    </Button>
                  </ButtonGroup>
                </div>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>

      <FilterSection
        title="Stock Overview"
        description="Opening Balance | Receipts | Distributions | Consumption | Balance"
        definitions={definitions}
        options={options}
        onChange={handleBarFilter}
        onReset={resetBarFilter}
        activeContexts={["item", "province_in", "province_out", "consumption"]}
      >
        {renderChart(barSection, ProviderDashboardBarChart, "stock")}
        {renderStockDetails(
          barFiltersRef,
          stockListOpen,
          setStockListOpen,
          "Show Stock Details",
          "Hide Stock Details"
        )}
      </FilterSection>

      <FilterSection
        title="Stock Breakdown"
        description="Stacked view of opening balance, receipts, distributions, consumption, and balance"
        definitions={definitions}
        options={options}
        onChange={handleStackedBarFilter}
        onReset={resetStackedBarFilter}
        activeContexts={["item", "province_in", "province_out", "consumption"]}
      >
        {renderStackedBarChart(stackedBarSection)}
        {renderStockDetails(
          stackedBarFiltersRef,
          stackedBarListOpen,
          setStackedBarListOpen,
          "Show Breakdown Details",
          "Hide Breakdown Details"
        )}
      </FilterSection>

      <FilterSection
        title="Monthly Trends"
        description="Monthly distribution by item (by month)"
        definitions={definitions}
        options={options}
        onChange={handleLineFilter}
        onReset={resetLineFilter}
        activeContexts={["item", "province_in", "province_out", "consumption"]}
      >
        {renderChart(lineSection, ProviderDashboardLineChart, "distributions")}
        {renderStockDetails(
          lineFiltersRef,
          lineListOpen,
          setLineListOpen,
          "Show Monthly Details",
          "Hide Monthly Details"
        )}
      </FilterSection>

      <FilterSection
        title="Category Distribution"
        description="Distribution breakdown by item, type, and category"
        definitions={definitions}
        options={options}
        onChange={handlePieFilter}
        onReset={resetPieFilter}
        activeContexts={["item", "province_in", "province_out", "consumption"]}
      >
        {renderChart(pieSection, ProviderDashboardPieChart, "stock")}
        {renderStockDetails(
          pieFiltersRef,
          pieListOpen,
          setPieListOpen,
          "Show Category Details",
          "Hide Category Details"
        )}
      </FilterSection>

      <FilterSection
        title="Province Map"
        description="Geographic distribution of receipts and distributions across provinces"
        definitions={definitions}
        options={options}
        onChange={handleMapFilter}
        onReset={resetMapFilter}
        activeContexts={["province_out", "consumption"]}
      >
        <AfghanistanProvinceMapPanel
          data={mapSection.data}
          loading={mapSection.loading}
          error={mapSection.error}
          mapType={localMapType}
          onTypeChange={handleMapTypeChange}
          onProvinceClick={handleProvinceClick}
        />
      </FilterSection>

      {categories?.data && (
        <div className="mt-4">
          <ProvinceStockComponents
            stocks={stocks}
            allData={allData}
            onCategoryChange={onCategoryChange}
            onAdvanceSearch={onAdvanceSearch}
            categories={categories}
            onExportClicked={onExportClicked}
            onAdvancedSearchHandle={onAdvancedSearchHandle}
          />
        </div>
      )}
    </div>
  )
}

export default ProvinceDashboardComponent
